import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  ButtonV3Component,
  ColumnDefinitionV3,
  ColumnRendererComponentV3,
  ComplexDialogV3Service,
  FilterTableSettings,
  InputRendererConfigV3,
  InputRendererV3Component,
  ModalV2Module,
  SnackbarV3Service,
  TableV3Component,
  TableServiceV3,
  TextFilterV3Component,
  PageHeaderV3Component,
  PageFooterComponent,
  PageContentLayoutV3Component,
} from '@gea/digital-ui-lib';
import { DataMaintenanceService } from '../data-maintenance.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DividerModule } from 'primeng/divider';
import { filter, finalize, switchMap } from 'rxjs';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  UnknownContent,
  DataTableContent,
  DataMaintenanceTableColumns,
  DataMaintenanceAsset,
  AssetConfigResponse,
} from '../data-maintenance.model';
import { ActivatedRoute, Router, RouteReuseStrategy } from '@angular/router';
import { NoRouteReuseStrategy } from 'src/app/shared/services/noroutereusestategy.service';
import { OptionsTableMenuComponent } from 'src/app/shared/components/options-menu/options-table-menu.component';
import { DataMaintenanceDialogComponent } from '../data-maintenance-dialog/data-maintenance-dialog.component';
import { RowStatusRendererComponent } from '../renderers/row-status-renderer.component';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-data-maintenance-content',
  imports: [
    ButtonV3Component,
    ModalV2Module,
    ReactiveFormsModule,
    DividerModule,
    ConfirmDialogModule,
    TableV3Component,
    PageHeaderV3Component,
    PageFooterComponent,
    PageContentLayoutV3Component,
  ],
  templateUrl: './data-maintenance-content.component.html',
  styleUrl: './data-maintenance-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TableServiceV3, { provide: RouteReuseStrategy, useClass: NoRouteReuseStrategy }],
})
export class DataMaintenanceContentComponent implements OnInit {
  readonly tableId = 'dataContentTable';
  path: string = '';
  fileName = '';
  count: number = 0;
  data: Array<{ [key: string]: string | number | boolean | null }> = [];
  filteredData: Array<{ [key: string]: string | number | boolean | null }> = [];
  content: DataTableContent<string> | UnknownContent | null = null;
  dataContentColumnDefinitions: ColumnDefinitionV3[] = [];
  isTableLoading = signal<boolean>(true);
  hasUnsavedChanges = signal<boolean>(false);
  hasValidationErrors = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  selectedRow: { [key: string]: string | number | boolean | null } | null = null;

  private static readonly numericTypes = ['number', 'double', 'decimal', 'float', 'int', 'integer'];
  private static readonly integerTypes = ['int', 'integer'];
  private static readonly booleanTypes = ['bool', 'boolean'];
  private static readonly defaultPageSize = 10;
  private static readonly rowErrorPattern = /^Row (\d+)\b/;

  private readonly pathsByRow = new WeakMap<object, string[]>();
  private readonly changedRows = signal(new Set<object>());
  private readonly errorsByRow = signal(new Map<object, string[]>());
  private asset: DataMaintenanceAsset | null = null;
  private assetConfig: AssetConfigResponse | null = null;
  canReplaceFile = false;
  private tableSettings: FilterTableSettings | null = null;
  private inferredColumnTypes: { [key: string]: string } = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataMaintenanceService: DataMaintenanceService,
    private dialogService: ComplexDialogV3Service,
    private tableService: TableServiceV3,
    private snackbarService: SnackbarV3Service,
    private utilityService: UtilityService,
    private destroyed: DestroyRef
  ) {}

  ngOnInit(): void {
    this.fetchDataContent();
    this.handleHeaderClick();
    this.handleMenuItemsClick();
    this.handleInlineEdits();
  }

  getUpdatedJson(): string {
    return JSON.stringify(this.content, null, 2);
  }

  saveChanges(): void {
    const asset = this.asset;
    const config = this.assetConfig;
    if (!asset || !config || !this.canReplaceFile || this.hasValidationErrors() || this.isSaving()) return;

    const categoryId = this.resolveCategoryId(config, asset.type);
    if (!categoryId) {
      this.snackbarService.add({
        severity: 'error',
        summary: 'Unable to save file',
        detail: `No upload category is configured for "${asset.type}".`,
      });
      return;
    }

    this.errorsByRow.set(new Map());
    this.hasValidationErrors.set(false);
    this.isSaving.set(true);

    const file = new File([this.getUpdatedJson()], asset.fileName, { type: 'application/json' });
    this.dataMaintenanceService
      .fileUploadOrReplace({
        file,
        categoryId,
        productName: asset.productName,
        targetFileName: asset.fileName,
      })
      .pipe(finalize(() => this.isSaving.set(false)), takeUntilDestroyed(this.destroyed))
      .subscribe({
        next: () => {
          this.changedRows.set(new Set());
          this.hasUnsavedChanges.set(false);
          this.snackbarService.add({ severity: 'success', summary: 'Success', detail: 'File saved successfully' });
        },
        error: (error: HttpErrorResponse) => this.applyValidationErrors(error),
      });
  }

  private resolveCategoryId(config: AssetConfigResponse, assetType: string): string | undefined {
    return Object.values(config.assetCategories).find((category) => category.pathPrefix === assetType)?.id;
  }

  private applyValidationErrors(error: HttpErrorResponse): void {
    const errors = (error?.error?.errors ?? {}) as { [key: string]: string[] };
    const errorsByRow = new Map<object, string[]>();

    for (const message of Object.values(errors).flat()) {
      const match = DataMaintenanceContentComponent.rowErrorPattern.exec(message);
      const row = match ? this.data[Number(match[1])] : undefined;
      if (!row) continue;

      errorsByRow.set(row, [...(errorsByRow.get(row) ?? []), message]);
    }

    this.errorsByRow.set(errorsByRow);
    this.hasValidationErrors.set(errorsByRow.size > 0);
  }

  private buildRowStatusColumn(): ColumnDefinitionV3 {
    return {
      key: 'rowStatus',
      displayName: '',
      colIndex: -1,
      width: 12,
      resizeable: false,
      sortable: false,
      renderer: {
        component: RowStatusRendererComponent as ColumnRendererComponentV3<{
          [key: string]: string | number | boolean | null;
        }>,
        config: {
          changedRows: this.changedRows.asReadonly(),
          errorsByRow: this.errorsByRow.asReadonly(),
        },
      },
    };
  }

  private buildTableEndColumn(): ColumnDefinitionV3 {
    return {
      key: 'tableEnd',
      displayName: '',
      width: 2,
      resizeable: false,
      sortable: false,
    };
  }

  buildColumnsFromContent(content: DataTableContent<string>): ColumnDefinitionV3[] {
    const baseColumns: ColumnDefinitionV3[] = content.tableDefinition.map((colDef, index) => {
      const { name, header, sortable, filterable, readonly } = colDef;
      return {
        key: name,
        displayName: header || name,
        sortable: sortable ?? false,
        filter: filterable
          ? {
              component: TextFilterV3Component,
            }
          : undefined,
        renderer: readonly || !this.canReplaceFile ? undefined : this.buildInputRenderer(colDef.type),
        resizeable: true,
        visible: true,
        colIndex: index,
      };
    });

    const settingsColumn: ColumnDefinitionV3 = {
      key: 'settings',
      width: 50,
      sortable: false,
      frozen: 'right',
      displayName: '',
      renderer: {
        component: OptionsTableMenuComponent<{ [key: string]: string | number | boolean | null }> as ColumnRendererComponentV3<{
          [key: string]: string | number | boolean | null;
        }>,
        config: {
          items: [{ icon: 'search', text: 'View', action: 'view' }],
        },
      },
    };

    return [
      ...(this.canReplaceFile ? [this.buildRowStatusColumn()] : []),
      ...baseColumns,
      settingsColumn,
    ];
  }

  private buildInputRenderer(columnType: string): ColumnDefinitionV3['renderer'] {
    return {
      component: InputRendererV3Component as ColumnRendererComponentV3<{
        [key: string]: string | number | boolean | null;
      }>,
      config: {
        type: DataMaintenanceContentComponent.integerTypes.includes(columnType?.toLowerCase()) ? 'number' : 'text',
      } as InputRendererConfigV3,
    };
  }

  private handleInlineEdits(): void {
    this.tableService.columnChanges
      .pipe(
        filter((change) => change.tableId === this.tableId),
        takeUntilDestroyed(this.destroyed)
      )
      .subscribe(({ column, value, rowData }) => this.applyCellChange(column, value, rowData));
  }

  private applyCellChange(
    column: string,
    value: string | null,
    rowData: { [key: string]: string | number | boolean | null }
  ): void {
    if (!this.canReplaceFile) return;

    const row = this.data.find((entry) => entry === rowData);
    if (!row) return;

    if (this.dataMaintenanceService.isDataTableContent(this.content)) {
      const columnDefinition = this.content.tableDefinition.find((definition) => definition.name === column);
      const coerced = this.coerceValue(value, columnDefinition);
      if (coerced === undefined) {
        this.setRowValidationError(
          row,
          this.getInvalidValueError(columnDefinition?.type, columnDefinition?.header || columnDefinition?.name || column)
        );
        return;
      }
      row[column] = coerced;
    } else {
      const coerced = this.coerceValue(value, { type: this.inferredColumnTypes[column] } as DataMaintenanceTableColumns);
      if (coerced === undefined) {
        this.setRowValidationError(row, this.getInvalidValueError(this.inferredColumnTypes[column], column));
        return;
      }
      row[column] = coerced;

      const path = this.pathsByRow.get(row);
      if (path?.length) {
        this.setValueAtPath(this.content as UnknownContent, path, this.parseUnknownValue(coerced));
      }
    }

    this.changedRows.update((changedRows) => new Set(changedRows).add(row));
    this.errorsByRow.update((errorsByRow) => {
      const updatedErrors = new Map(errorsByRow);
      updatedErrors.delete(row);
      return updatedErrors;
    });
    this.hasValidationErrors.set(this.errorsByRow().size > 0);
    this.hasUnsavedChanges.set(true);
  }

  private getInvalidValueError(columnType: string | undefined, columnName: string): string {
    return DataMaintenanceContentComponent.booleanTypes.includes(columnType?.toLowerCase() ?? '')
      ? `'${columnName}' must be true or false.`
      : `'${columnName}' must be a valid number.`;
  }

  private setRowValidationError(row: object, error: string): void {
    this.errorsByRow.update((errorsByRow) => new Map(errorsByRow).set(row, [error]));
    this.hasValidationErrors.set(true);
  }

  private coerceValue(
    value: string | null,
    columnDefinition?: DataMaintenanceTableColumns
  ): string | number | boolean | null | undefined {
    if (value === null || value === '') return null;

    const type = columnDefinition?.type?.toLowerCase() ?? '';
    if (DataMaintenanceContentComponent.numericTypes.includes(type)) {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    if (DataMaintenanceContentComponent.booleanTypes.includes(type)) {
      const normalized = value.trim().toLowerCase();
      if (normalized !== 'true' && normalized !== 'false') return undefined;
      return normalized === 'true';
    }

    return value;
  }

  private isObjectArray(content: unknown): content is Array<{ [key: string]: unknown }> {
    return (
      Array.isArray(content) &&
      content.length > 0 &&
      content.every((entry) => entry !== null && typeof entry === 'object' && !Array.isArray(entry))
    );
  }

  private buildColumnsFromObjectArray(entries: Array<{ [key: string]: unknown }>): ColumnDefinitionV3[] {
    const keys = new Set<string>();
    entries.forEach((entry) => Object.keys(entry).forEach((key) => keys.add(key)));

    this.inferredColumnTypes = {};
    keys.forEach((key) => {
      const sample = entries.find((entry) => entry[key] !== null && entry[key] !== undefined)?.[key];
      this.inferredColumnTypes[key] = typeof sample === 'number' ? 'number' : typeof sample === 'boolean' ? 'boolean' : 'string';
    });

    return [
      ...(this.canReplaceFile ? [this.buildRowStatusColumn()] : []),
      ...[...keys].map((key, index) => ({
        key,
        displayName: key,
        sortable: true,
        filter: { component: TextFilterV3Component },
        renderer: this.canReplaceFile ? this.buildInputRenderer(this.inferredColumnTypes[key]) : undefined,
        resizeable: true,
        visible: true,
        colIndex: index,
      })),
    ];
  }

  private flattenContent(
    value: unknown,
    path: string[],
    rows: Array<{ [key: string]: string | number | boolean | null }>
  ): void {
    if (value !== null && typeof value === 'object') {
      const entries = Array.isArray(value)
        ? value.map((item, index) => [String(index), item] as [string, unknown])
        : Object.entries(value as UnknownContent);

      if (entries.length) {
        entries.forEach(([key, child]) => this.flattenContent(child, [...path, key], rows));
        return;
      }
    }

    const row = {
      key: path.join('.'),
      value:
        value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
          ? value
          : JSON.stringify(value),
    };
    this.pathsByRow.set(row, path);
    rows.push(row);
  }

  private setValueAtPath(target: UnknownContent, path: string[], value: unknown): void {
    let current: unknown = target;
    for (const segment of path.slice(0, -1)) {
      current = (current as UnknownContent)[segment];
      if (current === null || typeof current !== 'object') return;
    }
    (current as UnknownContent)[path[path.length - 1]] = value;
  }

  private parseUnknownValue(value: string | number | boolean | null): unknown {
    if (typeof value !== 'string') return value;

    const trimmed = value.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;

    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  private handleMenuItemsClick(): void {
    this.tableService.actions.pipe(takeUntilDestroyed(this.destroyed)).subscribe((actionWrapper) => {
      this.selectedRow = actionWrapper.rowData;
      switch (actionWrapper.action) {
        case 'view': {
          this.showAddItemDialog(this.selectedRow);
          break;
        }
      }
    });
  }

  private handleHeaderClick(): void {
    this.tableService
      .getFilterTableSettings(this.tableId)
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe((settings) => {
        this.tableSettings = settings;
        this.applyTableSettings();
      });
  }

  private applyTableSettings(): void {
    const columns = this.tableSettings?.columns ?? {};
    const validKeys = Object.keys(columns).filter((k) => this.dataContentColumnDefinitions.some((col) => col.key === k));

    const sorted: { key: string; order: 'asc' | 'desc' }[] = [];
    const filters: { key: string; value: string | boolean }[] = [];

    for (const key of validKeys) {
      const { sort, filter } = columns[key];
      if (sort === 'asc' || sort === 'desc') sorted.push({ key, order: sort });
      if (filter != null) filters.push({ key, value: filter[0] });
    }

    const processedData = this.processData(filters, sorted);
    this.count = processedData.length;

    const pageSize = this.tableSettings?.pageSize ?? DataMaintenanceContentComponent.defaultPageSize;
    const page = this.tableSettings?.page ?? 0;
    const first = page * pageSize;
    this.filteredData =
      first >= processedData.length ? processedData.slice(0, pageSize) : processedData.slice(first, first + pageSize);
  }

  private processData(
    filters: { key: string; value: string | boolean }[],
    sorted: { key: string; order: 'asc' | 'desc' }[]
  ): Array<{ [key: string]: string | number | boolean | null }> {
    let result = this.data;

    if (filters.length) {
      result = result.filter((row) =>
        filters.every(({ key, value }) => {
          const rowValue = row[key];
          if (rowValue == null) return false;

          return typeof value === 'string'
            ? String(rowValue).toLowerCase().includes(value.toLowerCase())
            : rowValue === value;
        })
      );
    }

    if (sorted.length) {
      result = [...result].sort((a, b) =>
        sorted.reduce((acc, { key, order }) => {
          if (acc !== 0) return acc;
          const aVal = a[key],
            bVal = b[key];
          if (aVal === bVal) return 0;
          const comparison = aVal! < bVal! ? -1 : 1;
          return order === 'desc' ? -comparison : comparison;
        }, 0)
      );
    }

    return result;
  }

  private fetchDataContent(): void {
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          this.path = params.get('assetId') || '';
          return this.dataMaintenanceService.getConfig().pipe(
            switchMap((config) => {
              this.assetConfig = config;
              return this.dataMaintenanceService.getAsset(this.path);
            })
          );
        }),
        takeUntilDestroyed(this.destroyed)
      )
      .subscribe((assetResponse) => {
        this.asset = assetResponse;
        this.fileName = assetResponse.fileName;
        this.canReplaceFile = this.canReplaceAsset(assetResponse);
        if (this.dataMaintenanceService.isDataTableContent(assetResponse.content)) {
          this.content = assetResponse.content;
          this.data = this.content.data;
          this.dataContentColumnDefinitions = this.buildColumnsFromContent(this.content);
        } else if (this.isObjectArray(assetResponse.content)) {
          this.content = assetResponse.content as unknown as UnknownContent;
          this.data = assetResponse.content as Array<{ [key: string]: string | number | boolean | null }>;
          this.dataContentColumnDefinitions = this.buildColumnsFromObjectArray(assetResponse.content);
        } else {
          this.content = assetResponse.content as UnknownContent;
          const rows: Array<{ [key: string]: string | number | boolean | null }> = [];
          this.flattenContent(this.content, [], rows);
          this.data = rows;
          this.dataContentColumnDefinitions = [
            ...(this.canReplaceFile ? [this.buildRowStatusColumn()] : []),
            {
              key: 'key',
              displayName: 'Key',
              resizeable: true,
              filter: { component: TextFilterV3Component },
            },
            {
              key: 'value',
              displayName: 'Value',
              resizeable: true,
              renderer: this.canReplaceFile ? this.buildInputRenderer('string') : undefined,
            },
            this.buildTableEndColumn(),
          ] as ColumnDefinitionV3[];
        }
        this.isTableLoading.set(false);
        this.applyTableSettings();
      });
  }

  private canReplaceAsset(asset: DataMaintenanceAsset): boolean {
    const category = Object.values(this.assetConfig?.assetCategories ?? {}).find(
      (configuredCategory) => configuredCategory.pathPrefix === asset.type
    );
    if (!category?.validationRules.allowUpload) return false;

    return category.validationRules.allowedExtensions.some((extension) =>
      asset.fileName.toLowerCase().endsWith(extension.toLowerCase())
    );
  }

  showAddItemDialog(selectedRow: { [key: string]: string | number | boolean | null } | null): void {
    if (!this.canReplaceFile) return;

    const { ref } = this.dialogService.open(
      {
        header: 'Add data record',
        yes: 'Save',
        no: 'Cancel',
        hideButtons: false,
        data: { data: selectedRow, columns: this.content?.tableDefinition || [] },
        dismissableMask: true,
      },
      DataMaintenanceDialogComponent
    );
    ref.onClose.pipe(takeUntilDestroyed(this.destroyed)).subscribe((data: { [key: string]: string | number | boolean | null } | null) => {
      if (data && !this.utilityService.isRejectEventFromGEADialog(data) && !this.utilityService.isExitEventFromGEADialog(data)) {
        if (this.dataMaintenanceService.isDataTableContent(this.content)) {
          this.content!.data.push(data);
        } else if (Array.isArray(this.content)) {
          this.content.push(data);
        } else {
          Object.entries(data).forEach(([key, value]) => {
            (this.content as Record<string, unknown>)[key] = value;
          });
        }

        this.changedRows.update((changedRows) => new Set(changedRows).add(data));
        this.refreshContentData();
        this.hasUnsavedChanges.set(true);
      }
    });
  }

  private refreshContentData(): void {
    if (this.dataMaintenanceService.isDataTableContent(this.content)) {
      this.data = this.content.data;
      this.applyTableSettings();
      return;
    }

    if (this.isObjectArray(this.content)) {
      this.data = this.content as Array<{ [key: string]: string | number | boolean | null }>;
      this.dataContentColumnDefinitions = this.buildColumnsFromObjectArray(this.content);
      this.applyTableSettings();
      return;
    }

    const rows: Array<{ [key: string]: string | number | boolean | null }> = [];
    this.flattenContent(this.content, [], rows);
    this.data = rows;
    this.applyTableSettings();
  }

  goBack(): void {
    this.router.navigate(['/data-maintenance']);
  }

}
